import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled/macro';
import vars from '../../variables';
import useOnClickOutside from '../../hooks/use-on-click-outside';
import { H3Atom } from '../../atoms/text-atoms/text-atoms';
import FilterBox from './filter-box';
import Dropdown from './dropdown';
import CheckboxInput from './checkbox-input';
import CheckboxInputHeader from './checkbox-input-header';
import { DropdownIconAtom } from '../../atoms/icon-atoms/icon-atoms';
import { generateKey } from '../../utils/utils';
import { GTM_ID } from '../../../../common/constants/gtmIds';

const Filter = ({
  id,
  title = 'filter by',
  label,
  fields,
  isExact = true,
  checkboxData,
  updateQueryArray,
  width,
  matchDropdownWidth = true,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCheckboxes, updateSelectedCheckboxes] = useState([]);

  const allId = 'All';

  // setIsOpen(false) if user clicks outside filter
  const ref = useRef();
  useOnClickOutside(ref, () => setIsOpen(false));

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleCheckboxChange = event => {
    const checkboxId = event.target.id;
    toggleCheckbox(checkboxId);
  };

  const handleCheckboxHeaderChange = event => {
    const checkboxId = event.target.id;
    // Select all checkboxes (including the target one) in this Filter
    let query = `[data-group-id=${id}]`;
    if (checkboxId !== allId) {
      // Select all checkboxes (including the target one), who's ids start with the id of the target checkbox
      query += `[id^="${checkboxId}"]`;
    }
    const linkedCheckboxes = document.querySelectorAll(query);
    if (selectedCheckboxes.includes(checkboxId)) {
      linkedCheckboxes.forEach(element => {
        unselectCheckbox(element.id);
      });
    } else {
      linkedCheckboxes.forEach(element => {
        selectCheckbox(element.id);
      });
    }
  };

  const toggleCheckbox = checkboxId => {
    if (selectedCheckboxes.includes(checkboxId)) {
      updateSelectedCheckboxes(previousState =>
        previousState.filter(element => element !== checkboxId)
      );
    } else {
      updateSelectedCheckboxes(previousState => [...previousState, checkboxId]);
    }
  };

  const selectCheckbox = checkboxId => {
    if (!selectedCheckboxes.includes(checkboxId)) {
      updateSelectedCheckboxes(previousState => [...previousState, checkboxId]);
    }
  };

  const unselectCheckbox = checkboxId => {
    if (selectedCheckboxes.includes(checkboxId)) {
      updateSelectedCheckboxes(previousState =>
        previousState.filter(element => element !== checkboxId)
      );
    }
  };

  useEffect(() => {
    updateQueryArray(previousArray => {
      const filteredArray = previousArray.filter(element => element.id !== id);
      if (selectedCheckboxes.length) {
        return [
          ...filteredArray,
          {
            id,
            fields,
            isExact,
            queryStrings: selectedCheckboxes,
          },
        ];
      } else {
        return filteredArray;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCheckboxes]);

  if (Array.isArray(checkboxData)) {
    return (
      <div ref={ref} className={`${className} filter-block-element`}>
        {title && <H3Atom>{title}</H3Atom>}
        <FilterBox
          handleInteraction={toggleOpen}
          width={width}
          isClickable={true}
          gtmId={isOpen ? null : GTM_ID.FILTER_SELECT}
        >
          {label}
          <DropdownIconAtom isOpen={isOpen} />
        </FilterBox>
        <Dropdown
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          checkboxes={checkboxData}
          width={matchDropdownWidth && width ? width : null}
        >
          {checkboxData.map((checkbox, index) => {
            if (checkbox.type === 'all') {
              return (
                <CheckboxInputHeader
                  id={allId}
                  label={allId}
                  selectedCheckboxes={selectedCheckboxes}
                  handleChange={handleCheckboxHeaderChange}
                  dataGroupId={id}
                  key={generateKey(allId, index)}
                />
              );
            } else if (checkbox.type === 'header') {
              return (
                <CheckboxInputHeader
                  id={checkbox.id}
                  label={checkbox.label}
                  selectedCheckboxes={selectedCheckboxes}
                  handleChange={handleCheckboxHeaderChange}
                  dataGroupId={id}
                  key={generateKey(checkbox.label, index)}
                />
              );
            } else if (checkbox.type === 'standard') {
              return (
                <CheckboxInput
                  id={checkbox.id}
                  label={checkbox.label}
                  selectedCheckboxes={selectedCheckboxes}
                  handleChange={handleCheckboxChange}
                  isInset={checkbox.isInset}
                  dataGroupId={id}
                  key={generateKey(checkbox.label, index)}
                />
              );
            } else {
              console.warn(
                `filter.js -> checkbox type not recognised: ${checkbox.type}`
              );
              return null;
            }
          })}
        </Dropdown>
      </div>
    );
  } else {
    console.warn(
      `Filter received non valid argument - checkboxData: ${checkboxData}`
    );
    return null;
  }
};

// === Filter CSS ===
const StyledFilter = styled(Filter)`
  ${H3Atom} {
    ${vars.fontSize4}
    font-weight: ${vars.fontWeightRegular};
    margin: 0 0 ${vars.spacingXs} ${vars.spacingXs};
    &::first-letter {
      text-transform: uppercase;
    }
  }
  ${FilterBox} {
    justify-content: space-between;
  }
  ${DropdownIconAtom} {
    pointer-events: none;
  }
`;

export default StyledFilter;
